import React from 'react';

import TextLink from '@/components/Link/TextLink';
import InlineCode from '@/components/InlineCode';
import ServerOnlySnippet from '@/components/CodeSnippet/ServerOnlySnippet';

import type { Operator } from './OperatorLookup.types';

const DATA: Record<string, Operator> = {
  '=>': {
    label: 'Arrow',
    definition: (
      <>
        <p>
          While technically not an operator, this combination of
          characters is used in <strong>arrow functions</strong>.
        </p>
        <p>
          An arrow function is an alternative way of writing a
          function definition. It comes with a few small
          quality-of-life benefits.
        </p>
        <p>
          Arrow functions are somewhat limited: they don't have their
          own context (so <InlineCode>this</InlineCode> cannot be
          used), nor can they be used as constructors.
        </p>
      </>
    ),
    example: `
function regularFunction() {
  console.log("I'm a traditional function!")
}

const arrowFunction = () => {
  console.log("I'm an arrow function!")
}

// Arrow functions can omit the parentheses
// if they only take a single parameter:
const singleParam = num => {
  console.log(num);
}

// Arrow functions also have an implicit return
// if the braces are omitted:
const implicitReturn = () => 5;
console.log(implicitReturn()); // 5

// Arrow functions are convenient when used as
// callbacks for array methods:
const nums = [-2, -1, 0, 1, 2, 3];
const positiveNums = nums.filter(num => num >= 0);
console.log(positiveNums); // [0, 1, 2, 3]
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions',
  },

  '+': {
    label: 'Addition',
    definition: (
      <>
        <p>
          This operator adds two numbers together, or concatenates two
          strings together.
        </p>
        <p>
          If a number and a string are used together, it performs a
          concatenation.
        </p>
        <p>
          Less commonly, it can be used as a{' '}
          <TextLink href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Unary_plus">
            unary operator
          </TextLink>
          , to try and convert a string containing digits to a number.
          It's a somewhat obscure trick, and it's generally better to
          use the{' '}
          <TextLink href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number">
            Number()
          </TextLink>{' '}
          helper instead.
        </p>
      </>
    ),
    example: `
console.log(1 + 1); // 2
console.log('hello' + 'world');
// 'helloworld'

console.log(1 + '1'); // '11'

// As a unary operator
console.log(+'5'); // 5
console.log(+'hello'); // NaN
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Addition',
  },

  '+=': {
    label: 'Addition Assignment',
    definition: (
      <>
        <p>
          This operator adds the supplied value to a variable, and
          overwrites that variable's value with the result.
        </p>
        <p>
          This is one of those things which is hard to explain in
          words, but hopefully these examples help clarify!
        </p>
      </>
    ),
    example: `
let x = 10;
let y = 10;

x += 1;
console.log(x); // 11 (10 + 1)

y += 5;
console.log(y); // 15 (10 + 5)
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Addition_assignment',
  },

  '=': {
    label: 'Assignment',
    definition: (
      <>
        <p>This operator assigns a value to a variable.</p>
        <p>
          Not to be confused with the Equality (
          <InlineCode>==</InlineCode>) or Strict Equality (
          <InlineCode>===</InlineCode>) operators!
        </p>
      </>
    ),
    example: `
let hi = 5;
let hello = 'world';
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Assignment',
  },

  '&': {
    label: 'Bitwise AND',
    definition: (
      <>
        <p>
          This operator returns a 1 in each bit position for which the
          corresponding bits of both operands are 1s.
        </p>
        <p>
          Not to be confused with the Logical AND operator (
          <InlineCode>{'&&'}</InlineCode>)
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_AND',
  },

  '&=': {
    label: 'Bitwise AND Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise AND operation to a
          variable, and overwrites the result into the same variable.
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_AND_assignment',
  },

  '~': {
    label: 'Bitwise NOT',
    definition: (
      <>
        <p>
          This operator inverts the bits of the value it's called
          with.
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_NOT',
  },

  '|': {
    label: 'Bitwise OR',
    definition: (
      <>
        <p>This operator performs a bitwise OR operation.</p>
        <p>
          Not to be confused with the Logical OR operator (
          <InlineCode>||</InlineCode>)
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_OR',
  },

  '|=': {
    label: 'Bitwise OR Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise OR operation on a variable,
          and assigns the result to that same variable.
        </p>

        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_OR_assignment',
  },

  '^': {
    label: 'Bitwise XOR',
    definition: (
      <>
        <p>
          This operator performs a bitwise XOR operation on a
          variable.
        </p>

        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_XOR',
  },

  '^=': {
    label: 'Bitwise XOR Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise XOR operation on a
          variable, and assigns the result to that same variable.
        </p>

        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_XOR_assignment',
  },

  ',': {
    label: 'Comma',
    definition: (
      <>
        <p>
          The comma operator evaluates each in a sequence of values,
          and returns the final value.
        </p>
        <p>
          Note that the comma character isn't usually used as an
          operator; when passing multiple arguments to a function, for
          example, the comma character is <em>not</em> serving as an
          operator. That's a totally unrelated thing.
        </p>
      </>
    ),
    example: `
const x = (2, 3);

console.log(x); // 3
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Comma_Operator',
  },

  '?': {
    label: 'Ternary',
    definition: (
      <>
        <p>
          The ternary operator is unique to Javascript in that it
          requires two separate pseudo-operators,{' '}
          <InlineCode>?</InlineCode> and <InlineCode>:</InlineCode>.
        </p>
        <p>
          It is used to evaluate a condition, and return a different
          value depending on whether that condition is truthy or
          falsy. It's functionally equivalent to an if/else statement,
          but because it's an operator, it's usable in an expression.
          This allows it to be useful in a broader range of situations
          (eg. within JSX in React).
        </p>
      </>
    ),
    example: `
console.log(10 ? 'yes' : 'no');
// 'yes', since 10 is truthy

console.log(0 ? 'yes' : 'no');
// 'no', since 0 is falsy

// Can be used alongside other operators:
console.log(
  (5 < 10) ? 'smaller' : 'larger'
); // 'smaller'
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Conditional_Operator',
  },

  '--': {
    label: 'Decrement',
    definition: (
      <>
        <p>
          This operator is meant to be used on variables that hold
          numbers. When used, it decreases that variable's value by 1.
        </p>
        <p>
          This is commonly used in conjunction with{' '}
          <InlineCode>while</InlineCode> loops, or anywhere that
          requires a counter.
        </p>
        <p>
          It's functionally equivalent to{' '}
          <InlineCode>x = x - 1;</InlineCode>
        </p>
      </>
    ),
    example: `
let x = 5;

x--;
console.log(x); // 4
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Decrement',
  },

  '/': {
    label: 'Division',
    definition: (
      <>
        <p>
          This operator performs a mathematical division operation. It
          can be thought of as an easier-to-type division symbol (÷)
        </p>
      </>
    ),
    example: `
console.log(10 / 2); // 5
console.log(5 / 4); // 1.25`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Division',
  },

  '/=': {
    label: 'Division Assignment',
    definition: (
      <>
        <p>
          This operator performs a mathematical division operation on
          a variable, and assigns the result to that variable.
        </p>
      </>
    ),
    example: `
let x = 6;
x /= 3;

console.log(x); // 2
    `,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Division_assignment',
  },

  '**': {
    label: 'Exponentiation',
    definition: (
      <>
        <p>
          This operator multiplies a value side by itself a specified
          number of times. It's the modern equivalent to{' '}
          <InlineCode>Math.pow()</InlineCode>.
        </p>
      </>
    ),
    example: `
let x = 2;

console.log(2 ** 2); // 4
console.log(2 ** 3); // 8
console.log(2 ** 4); // 16

console.log(2 ** 0); // 1
    `,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Exponentiation',
  },

  '**=': {
    label: 'Exponentiation Assignment',
    definition: (
      <>
        <p>
          This operator applies an exponentiation (bringing the value
          to the power of X), an assigns the result to the affected
          variable. It's analogous to other assignment operators like
          the Addition Assignment (<InlineCode>+=</InlineCode>)
          operator.
        </p>
      </>
    ),
    example: `
let x = 2;

x **= 4;
console.log(x); // 16 (2 ** 4)
    `,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Exponentiation_assignment',
  },

  '<<': {
    label: 'Left Shift',
    definition: (
      <>
        <p>This operator performs a bitwise shift operation.</p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Left_shift',
  },

  '<<=': {
    label: 'Left Shift Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise shift operation on a
          variable, and overwrites the variable with this new value.
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Left_shift_assignment',
  },

  '&&': {
    label: 'Logical AND',
    definition: (
      <>
        <p>
          This operator is commonly used to check if all supplied
          values are truthy.
        </p>
        <p>
          It also works as a control-flow operator: it will return the
          first falsy value. If none of the values are falsy, it will
          return the final value.
        </p>
      </>
    ),
    example: `
// As a logical operator:
if (someCondition && someOtherCondition) {
  // Code here is only run if both
  // variables are true, or truthy.
}

// As a control flow operator:
console.log(0 && 4);
// 0, since it's the first falsy value

console.log(2 && 4);
// 4, since neither value is falsy

console.log(1 && 2 && 3);
// 3, since all values are truthy

console.log('a' && '' && 0);
// '', the first falsy value
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_AND',
  },

  '&&=': {
    label: 'Logical AND Assignment',
    definition: (
      <>
        <p>This is a funky one!</p>
        <p>
          It will assign a value to a variable, but only if that
          variable already holds a truthy value.
        </p>
      </>
    ),
    example: `
let x = 0;
let y = 1;

x &&= 2; // No effect, since 0 is falsy
y &&= 2; // Assigns 2, since 1 is truthy

console.log(x, y); // 0, 2
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_AND_assignment',
  },

  '!': {
    label: 'Logical NOT',
    definition: (
      <>
        <p>
          This operator, colloquially known as a bang, flips a boolean
          value. False becomes true, true becomes false.
        </p>
        <p>
          It also works on non-boolean values. Any falsy value will
          evaluate to <InlineCode>true</InlineCode>, and likewise any
          truthy value will evaluate to <InlineCode>false</InlineCode>
          .
        </p>
        <p>
          It can be repeated to turn any truthy value to true, and any
          falsy value to false, as shown below:
        </p>
      </>
    ),
    example: `
console.log(!true); // false
console.log(!false); // true

console.log(!0); // true
console.log(!10); // false

console.log(!!0); // false
console.log(!!10); // true
`,
    disclaimer: (
      <p>
        When placed at the <em>end</em> of a variable, this symbol
        acts as the TypeScript{' '}
        <TextLink href="https://www.typescriptlang.org/docs/handbook/2/everyday-types.html#non-null-assertion-operator-postfix-">
          Non-null Assertion Operator
        </TextLink>{' '}
        instead.
      </p>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_NOT',
  },

  '||': {
    label: 'Logical OR',
    definition: (
      <>
        <p>
          This operator is commonly used to check if either supplied
          value is truthy.
        </p>
        <p>
          It also works as a control-flow operator: it will return the
          first truthy value. If none of the values are truthy, it
          will return the final value.
        </p>
      </>
    ),
    example: `
// ---> As a logical operator:

if (someCondition || someOtherCondition) {
  // Code here is only run if at least one of
  // the two variables hold truthy values.
}

// ---> As a control flow operator:

console.log(0 || 4);
// 4, since it's the first truthy value

console.log(2 || 4);
// 2, since it's the first truthy value

console.log('' || 0);
// 0, since no values are truthy

console.log(1 || 2 || 3);
// 1, since it found a truthy value right away!
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR',
  },

  '||=': {
    label: 'Logical OR Assignment',
    definition: (
      <>
        <p>
          This is a funky one, and not just because it makes a fun
          emoticon face!
        </p>
        <p>
          It will assign a value to a variable, but only if that
          variable already holds a falsy value.
        </p>
      </>
    ),
    example: `
let x = 0;
let y = 1;

x ||= 2;
// Assigns 2, since 0 is falsy

y ||= 2;
// Has no effect, since 'y' held a truthy value

console.log(x, y); // 2, 1
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_OR_assignment',
  },

  '??=': {
    label: 'Logical Nullish Assignment',
    definition: (
      <>
        <p>
          This operator will update a variable with a new value, but
          only if that variable currently holds a "nullish" value
          (either <InlineCode>null</InlineCode> or{' '}
          <InlineCode>undefined</InlineCode>).
        </p>
        <p>
          This is a relatively new addition to the language (Chrome
          85, Firefox 79, Safari 14). No IE support.
        </p>
      </>
    ),
    example: `
let x = 0;
let y = null;
let z;

x ??= 2;
// Has no effect, since 'x' already holds a value

y ??= 2;
// Updates 'y' to hold this new value

z ??= 2;
// Updates 'z' to hold this new value
// (by not assigning 'z' a value initially, it
// was considered undefined).

console.log(x, y, z); // 0, 2, 2
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_nullish_assignment',
  },

  '*': {
    label: 'Multiplication',
    definition: (
      <>
        <p>
          This operator performs a mathematical multiplication
          operation. It can be thought of as an easier-to-type
          multiplication symbol (× or ⋅).
        </p>
      </>
    ),
    example: `
console.log(10 * 2); // 20
console.log(1.25 * 4); // 5`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Multiplication',
  },

  '*=': {
    label: 'Multiplication Assignment',
    definition: (
      <>
        <p>
          This operator performs a mathematical multiplication
          operation on a variable, and assigns the result to that
          variable.
        </p>
      </>
    ),
    example: `
let x = 6;
x *= 3;

console.log(x); // 18
    `,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Multiplication_assignment',
  },

  '??': {
    label: 'Nullish Coalescing',
    definition: (
      <>
        <p>
          This relatively-new addition to the language is similar to
          the Logical OR operator (<InlineCode>||</InlineCode>),
          except instead of relying on truthy/falsy values, it relies
          on "nullish" values (there are only 2 nullish values,{' '}
          <InlineCode>null</InlineCode> and{' '}
          <InlineCode>undefined</InlineCode>).
        </p>
        <p>
          This means it's safer to use when you treat falsy values
          like <InlineCode>0</InlineCode> as valid.
        </p>
        <p>
          Similar to Logical OR, it functions as a control-flow
          operator; it evaluates to the first not-nullish value.
        </p>
        <p>
          It was introduced in Chrome 80 / Firefox 72 / Safari 13.1.
          It has no IE support.
        </p>
      </>
    ),
    example: `
console.log(4 ?? 5);
// 4, since neither value is nullish

console.log(null ?? 10);
// 10, since 'null' is nullish

console.log(undefined ?? 0);
// 0, since 'undefined' is nullish

// Here's a case where it differs from
// Logical OR (||):
console.log(0 ?? 5); // 0
console.log(0 || 5); // 5
    `,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing_operator',
  },

  '?.': {
    label: 'Optional Chaining',
    definition: (
      <>
        <p>
          This operator is similar to the Property Accessor operator (
          <InlineCode>.</InlineCode>); it accesses a property on an
          object.
        </p>
        <p>
          The difference is that it's safe to chain; if at any point,
          a nullish value (<InlineCode>null</InlineCode> or{' '}
          <InlineCode>undefined</InlineCode>) is surfaced, the chain
          short-circuits and returns{' '}
          <InlineCode>undefined</InlineCode>.
        </p>
        <p>
          It can also be used in a couple surprising and neat ways,
          documented below:
        </p>
      </>
    ),
    example: `
const someObject = {}

console.log(someObject.prop.thing);
// Uncaught TypeError: Cannot read property
//                     'thing' of undefined

console.log(someObject?.prop?.thing);
// undefined

const obj = {
  hi: {
    hello: 5,
  },
};

// When the property name is stored in a variable:
const outerKey = 'hi';
const innerKey = 'hello';
console.log(obj?.[outerKey]?.[innerKey]); // 5

// When the property in question is a function
const dog = {
  woof() { return 'Woof!' }
};

console.log(dog.woof?.()); // "Woof!"
console.log(dog.meow?.()); // undefined
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Optional_chaining',
  },

  '|>': {
    label: 'Pipeline',
    definition: (
      <>
        <p>
          This operator is currently a{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://github.com/tc39/proposal-pipeline-operator"
          >
            Stage 2 proposed feature
          </a>
          ; it doesn't exist yet, in any browser.
        </p>
        <p>
          It functions as a sort of "inverted" function call; instead
          of calling a function with an argument, you "pipe" an
          argument into a function.
        </p>
        <p>
          This is useful in certain functional-programming paradigms;
          it provides a nicer interface for currying, for example.
        </p>
      </>
    ),
    example: `
// Typical function call:
alert('hello');

// Piped alternative:
'hello' |> alert

`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Pipeline_operator',
  },

  '.': {
    label: 'Property Accessor',
    definition: (
      <>
        <p>
          When accessing a property on an object, you can use dot
          notation to pluck it out. This only works if you know the
          key of the property; if that property name is held in a
          variable, you'll need to use bracket notation instead.
        </p>
      </>
    ),
    example: `
const me = {
  name: 'Josh C.',
  address: {
    street: '123 Street Ave.',
    city: 'Montréal',
    province: 'Québec',
    country: 'Canada'
  }
}

console.log(me.name); // 'Josh C.'
console.log(me.address.city); // 'Montréal'

// If the key is held in a variable,
// I need to use brackets.
const relevantField = 'city';

console.log(me.address.relevantField);
// undefined

console.log(me.address[relevantField]);
// 'Montréal'
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Property_accessors',
  },

  '%': {
    label: 'Remainder (Modulo)',
    definition: (
      <>
        <p>
          This operator is in the same mathematical family as Addition
          (<InlineCode>+</InlineCode>) or Multiplication (
          <InlineCode>*</InlineCode>).
        </p>
        <p>
          It evaluates to the remainder of dividing the left-hand
          value by the right-hand.
        </p>
        <p>
          I have an entire blog post about this operator:{' '}
          <TextLink href="/javascript/modulo-operator">
            Understanding the JavaScript Modulo Operator
          </TextLink>
        </p>
      </>
    ),
    example: `
console.log(8 % 2);
// 0 — 2 fits perfectly into 4, so the
// remainder is '0'.

console.log(8 % 7);
// 1 — 7 can only go into 8 once, and
// there's 1 left over; imagine taking
// 7 pieces of pizza, and seeing 1 left.

`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Remainder',
  },

  '%=': {
    label: 'Remainder (Modulo) Assignment',
    definition: (
      <>
        <p>
          This operator is the "assignment" version of the remainder
          operator (<InlineCode>%</InlineCode>). It performs the
          operation on a variable, and assigns the result to that
          variable.
        </p>
      </>
    ),
    example: `
let hi = 5;

hi %= 4;
console.log(hi); // 1
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Remainder_assignment',
  },

  '>>': {
    label: 'Right Shift',
    definition: (
      <>
        <p>This operator performs a bitwise shift operation.</p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Right_shift',
  },

  '>>=': {
    label: 'Right Shift Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise shift operation on a
          variable, and overwrites the variable with this new value.
        </p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Right_shift_assignment',
  },

  '...': {
    label: 'Rest/Spread',
    definition: (
      <>
        <p>
          Alright, so this is a weird one. It's technically not an
          operator, it's special syntax we can use. And this syntax
          serves two mirror purposes:
        </p>
        <p>
          <strong>Rest:</strong> This syntax is used in function
          definitions to collect additional function arguments. Useful
          when you don't know how many parameters a function needs. It
          collects them into an array.
        </p>
        <p>
          <strong>Spread:</strong> This syntax performs the opposite
          of "rest", and can be used to populate a function from an
          array. It can also be used to clone or merge arrays and
          objects.
        </p>
      </>
    ),
    example: `
// --- Rest:
function collectNums(...numbers) {
  console.log(numbers);
}

collectNums(1, 2, 3, 4); // [1, 2, 3, 4]

// --- Spread:
function addNums(a, b) {
  console.log(a + b);
}

const myTwoNums = [1, 1];
addNums(...myTwoNums); // logs "2"

// Cloning an array
const data = [1, 2, 3];
const dataCopy = [...data];
const zeroIndexedData = [0, ...data];
// [0, 1, 2, 3]

// Merging an object
const obj = { hi: 5, bye: 10 };
const newObj = {...obj, bye: 20};
console.log(newObj); // { hi: 5, bye: 20 };
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax',
  },

  '-': {
    label: 'Subtraction',
    definition: (
      <>
        <p>
          This operator subtracts one number from another. It's a
          mathematical operator.
        </p>
        <p>
          As a unary operator, it can also be used to signify that a
          number is negative—for example<InlineCode>-5</InlineCode>{' '}
          isn't subtracting anything, it's indicating that this
          number's value is negative 5.
        </p>
      </>
    ),
    example: `
console.log(4 - 1); // 3

// As a unary operator
const negativeNum = -5;
console.log(5 + negativeNum); // 0
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Subtraction',
  },

  '-=': {
    label: 'Subtraction Assignment',
    definition: (
      <>
        <p>
          This operator subtracts the supplied value from a variable,
          and overwrites that variable's value with the result.
        </p>
        <p>
          This is one of those things which is hard to explain in
          words, but hopefully these examples help clarify!
        </p>
      </>
    ),
    example: `
let x = 10;
let y = 10;

x -= 1;
console.log(x); // 9 (10 - 1)

y -= 5;
console.log(y); // 5 (10 - 5)
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Subtraction_assignment',
  },

  '>>>': {
    label: 'Unsigned Right Shift',
    definition: (
      <>
        <p>This operator performs a bitwise shift operation.</p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Unsigned_right_shift',
  },

  '>>>=': {
    label: 'Unsigned Right Shift Assignment',
    definition: (
      <>
        <p>
          This operator performs a bitwise shift operation on a
          variable, and overwrites the variable with this new value.
        </p>
        <p>I think this is the only 4-character JS operator!</p>
        <p>
          Honestly, I don't really understand Bitwise operators;
          hopefully this MDN link will help you out:
        </p>
      </>
    ),
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Unsigned_right_shift_assignment',
  },

  '==': {
    label: 'Equality',
    definition: (
      <>
        <p>
          This operator checks to see if two values are equivalent.
          Returns a boolean value.
        </p>
        <p>
          Unlike the <em>strict equality operator</em> (
          <InlineCode>===</InlineCode>), this operator ignores the
          type and focuses exclusively on the value. For example, the
          number <InlineCode>2</InlineCode> is considered equivalent
          to the string "2".
        </p>
      </>
    ),
    example: `
// The numbers are different,
// so they aren't equal
console.log(10 == 11); // false

// The numbers are the same, so it is equal!
console.log(10 == 10); // true

// The value matches, regardless of type
console.log(10 == '10'); // true`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Equality',
  },

  '===': {
    label: 'Strict Equality',
    definition: (
      <>
        <p>
          This operator checks to see if two values are equivalent.
          Returns a boolean value.
        </p>
        <p>
          Unlike the <em>equality operator</em> (
          <InlineCode>==</InlineCode>), this operator checks the type
          as well as the value. For example, the number{' '}
          <InlineCode>2</InlineCode> is not considered equivalent to
          the string "2".
        </p>
      </>
    ),
    example: `
// The numbers are different,
// so they aren't equal
console.log(10 === 11); // false

// The numbers are the same,
// so it is equal!
console.log(10 === 10); // true

// The values are the same,
// but the type is different
console.log(10 === '10'); // false`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Strict_equality',
  },

  '!=': {
    label: 'Inequality',
    definition: (
      <>
        <p>
          This operator returns <InlineCode>true</InlineCode> if each
          side represents a different value
        </p>
        <p>
          Unlike the <em>strict inequality operator</em> (
          <InlineCode>!==</InlineCode>), the value's type isn't
          considered. For example, the number{' '}
          <InlineCode>2</InlineCode> is considered equivalent to the
          string "2", so the expression{' '}
          <InlineCode>2 != '2'</InlineCode> returns{' '}
          <InlineCode>false</InlineCode>. This is generally a bad
          thing, and it's recommended to use the{' '}
          <strong>strict inequality operator</strong> instead.
        </p>
      </>
    ),
    example: `
const a = 10;

// The numbers are different,
// so they are inequal
console.log(a != 11); // true

// The numbers are the same,
// they are not inequal
console.log(a != 10); // false

// Even though the types are different,
// the values are the same, so they're
// considered equal.
console.log(a != '10'); // false`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Inequality',
  },
  '!==': {
    label: 'Strict Inequality',
    definition: (
      <>
        <p>
          This operator checks to see if two values are not
          equivalent. Returns a boolean value.
        </p>
        <p>
          Unlike the <em>inequality operator</em> (
          <InlineCode>!=</InlineCode>), this operator considers the
          type as well as the value. For example, the number{' '}
          <InlineCode>2</InlineCode> is not considered equivalent to
          the string "2".
        </p>
      </>
    ),
    example: `
const a = 10;

// The numbers are different,
// so they are inequal
console.log(a !== 11); // true

// The numbers are the same,
// so they're not inequal
console.log(a !== 10); // false

// The values are the same,
// but the type is different,
// so they are still considered inequal
console.log(a !== '10'); // true`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Strict_inequality',
  },
  '>': {
    label: 'Greater Than',
    definition: (
      <>
        <p>
          This operator checks to see if the value on the left is
          larger than the value on the right.
        </p>
        <p>
          For numbers, this works as you'd probably expect. For
          strings, things can be surprising; each character is
          converted to its appropriate character code. This means that
          casing matters, as shown in the examples below.
        </p>
        <p>
          <strong>Protip:</strong> Struggling to distinguish this from
          the Less Than (<InlineCode>{'<'}</InlineCode>) operator?
          Pretend it's an alligator's mouth. It's greedy, and wants to
          eat whichever value is bigger.
        </p>
      </>
    ),
    example: `
console.log(10 > 5); // true
console.log(10 > 15); // false
console.log(-20 > 15); // false
console.log(0.5 > 1); // false
console.log(1 > 1); // false

console.log('b' > 'a'); // true
console.log('B' > 'a'); // false`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Greater_than',
  },
  '<': {
    label: 'Less Than',
    definition: (
      <>
        <p>
          This operator checks to see if the value on the left is
          smaller than the value on the right.
        </p>
        <p>
          For numbers, this works as you'd probably expect. For
          strings, things can be surprising; each character is
          converted to its appropriate character code. This means that
          casing matters, as shown in the examples below.
        </p>
        <p>
          <strong>Protip:</strong> Struggling to distinguish this from
          the Greater Than (<InlineCode>{'>'}</InlineCode>) operator?
          Pretend it's an alligator's mouth. It's greedy, and wants to
          eat whichever value is bigger.
        </p>
      </>
    ),
    example: `
console.log(10 < 5); // false
console.log(10 < 15); // true
console.log(-20 < 15); // true
console.log(0.5 < 1); // true
console.log(1 < 1); // false

console.log('b' < 'a'); // false
console.log('B' < 'a'); // true`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Less_than',
  },
  '>=': {
    label: 'Greater Than or Equal To',
    definition: (
      <>
        <p>
          This operator checks to see if the value on the left is
          larger than, or the same as, the value on the right.
        </p>
        <p>
          For numbers, this works as you'd probably expect. For
          strings, things can be surprising; each character is
          converted to its appropriate character code. This means that
          casing matters, as shown in the examples below.
        </p>
        <p>
          <strong>Protip:</strong> Struggling to distinguish this from
          the Less Than or Equal (<InlineCode>{'<='}</InlineCode>)
          operator? Pretend it's an alligator's mouth. It's greedy,
          and wants to eat whichever value is bigger.
        </p>
      </>
    ),
    example: `
console.log(10 >= 5); // true
console.log(10 >= 15); // false
console.log(-20 >= 15); // false
console.log(0.5 >= 1); // false
console.log(1 >= 1); // true

console.log('b' >= 'a'); // true
console.log('B' >= 'a'); // false`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Greater_than_or_equal',
  },
  '<=': {
    label: 'Less Than or Equal To',
    definition: (
      <>
        <p>
          This operator checks to see if the value on the left is
          smaller than, or the same as, the value on the right.
        </p>
        <p>
          For numbers, this works as you'd probably expect. For
          strings, things can be surprising; each character is
          converted to its appropriate character code. This means that
          casing matters, as shown in the examples below.
        </p>
        <p>
          <strong>Protip:</strong> Struggling to distinguish this from
          the Greater Than or Equal To(<InlineCode>{'>='}</InlineCode>
          ) operator? Pretend it's an alligator's mouth. It's greedy,
          and wants to eat whichever value is bigger.
        </p>
      </>
    ),
    example: `
console.log(10 <= 5); // false
console.log(10 <= 15); // true
console.log(-20 <= 15); // true
console.log(0.5 <= 1); // true
console.log(1 <= 1); // true

console.log('b' <= 'a'); // false
console.log('B' <= 'a'); // true`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Less_than_or_equal',
  },
  '++': {
    label: 'Increment',
    definition: (
      <>
        <p>
          This operator is meant to be used on variables that hold
          numbers. When used, it increases that variable's value by 1.
        </p>
        <p>
          This is commonly used in conjunction with{' '}
          <InlineCode>while</InlineCode> loops, or anywhere that
          requires a counter.
        </p>
        <p>
          It's functionally equivalent to{' '}
          <InlineCode>x = x + 1;</InlineCode>
        </p>
      </>
    ),
    example: `
let x = 5;

x++;
console.log(x); // 6
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Increment',
  },
  '!!': {
    label: 'Double “NOT”',
    definition: (
      <>
        <p>So: this isn't actually an operator!</p>
        <p>
          <InlineCode>!!</InlineCode> is a repetition of the{' '}
          <TextLink href="https://www.joshwcomeau.com/operator-lookup?match=logical-not">
            <InlineCode>!</InlineCode> (NOT) operator
          </TextLink>
          . This is effectively a way to convert a truthy/falsy value
          into its boolean value (<InlineCode>true</InlineCode> or{' '}
          <InlineCode>false</InlineCode>).
        </p>
      </>
    ),
    example: `
const truthyValue = 5;

console.log(!truthyValue); // false
console.log(!!truthyValue); // true
console.log(!!!truthyValue); // false
console.log(!!!!truthyValue); // true
`,
    link: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Logical_NOT',
  },
};

export default DATA;
